import { Controller } from '@hotwired/stimulus'

// Weeks index page. Handles the async "load more future weeks" button, the
// past-week finder select and pre-filling the edit-week-name modal before it
// is opened by the shared `modal` controller (registered on the same wrapper).
export default class extends Controller {
  static targets = ['futureBody', 'loadMore', 'editForm', 'editTag', 'editWeekLabel']

  connect() {
    this.syncLoadMore()
  }

  // Load another 7 future weeks and swap the future tbody, so the page never
  // reloads. The current week and past weeks live in a separate tbody below,
  // so they are left untouched.
  async loadMore() {
    if (!this.hasLoadMoreTarget || !this.hasFutureBodyTarget) return

    const button = this.loadMoreTarget
    const limit = parseInt(button.dataset.futureLimit, 10) || 7
    const next = limit + 7

    const url = new URL('/weeks', window.location.origin)
    url.searchParams.set('partial', 'future_weeks')
    url.searchParams.set('future_limit', String(next))

    this.futureBodyTarget.classList.add('is-loading')
    try {
      const response = await fetch(url, { headers: { Accept: 'text/html' } })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const html = await response.text()
      this.futureBodyTarget.innerHTML = html
      button.dataset.futureLimit = String(next)
      this.syncLoadMore()
    } catch (error) {
      // Fall back to a full navigation so the user still gets a result/error.
      window.location.assign(url)
    } finally {
      this.futureBodyTarget.classList.remove('is-loading')
    }
  }

  // Hide the load-more button once every future week is already shown.
  syncLoadMore() {
    if (!this.hasLoadMoreTarget) return

    const button = this.loadMoreTarget
    const limit = parseInt(button.dataset.futureLimit, 10) || 7
    const total = parseInt(button.dataset.futureTotal, 10) || 0
    button.classList.toggle('hidden', limit >= total)
  }

  // Runs before `modal#open` (actions run in the order listed in data-action),
  // so the shared modal is pointed at the right week before it appears.
  prepareEditModal(event) {
    const button = event.currentTarget

    this.editFormTarget.action = button.dataset.weekUrl
    this.editTagTarget.value = button.dataset.weekTag || ''

    if (this.hasEditWeekLabelTarget) {
      this.editWeekLabelTarget.textContent = button.dataset.weekLabel || ''
    }
  }

  // The past-week finder select navigates straight to the chosen week.
  goToPastWeek(event) {
    const id = event.target.value
    if (id) {
      window.location = `/weeks/${id}`
    }
  }
}
